import { useState, useEffect } from 'react';
import {
  Stack,
  Container,
  Typography,
  Card,
  Box,
  CardContent,
  CardActions,
  Button,
  Grid,
  Paper,
  Chip,
  alpha,
  useTheme,
  LinearProgress,
  Divider
} from '@mui/material';
import { getApi, postApi } from '../services/api';
import PaymentModel from '../../ui-component/PaymentModel';
import { toast } from 'react-toastify';
import {
  CardMembership as SubscriptionIcon,
  CheckCircle as CheckIcon,
  Star as StarIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  LocalOffer as DiamondIcon,
  Business as BusinessIcon,
  Verified as VerifiedIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon
} from '@mui/icons-material';

const HotelSubscriptionsDetails = () => {
  const theme = useTheme();
  const hotelId = JSON.parse(localStorage.getItem('hotelData'))._id;

  const [subscriptionData, setSubscriptionData] = useState([]);
  const [openPayment, setOpenPayment] = useState(false);
  const [purchaseData, setPurchaseData] = useState('');
  const [successfulPayments, setSuccessfulPayments] = useState([]);

  const handleOpenPayment = () => setOpenPayment(true);
  const handleClosePayment = () => setOpenPayment(false);

  const getAllSubscriptionData = async () => {
    // activeOnly=true → el backend solo devuelve planes activos.
    // El filtro local es un respaldo por si el server aún no soporta el query.
    const response = await getApi(`api/packages/getAllPackages?activeOnly=true`);
    if (response && response.status === 200) {
      const packages = response?.data?.packagesAllData || [];
      setSubscriptionData(packages.filter((pkg) => pkg.isActive !== false));
    } else {
      setSubscriptionData([]);
    }
  };

  const getAllPaymentsDetails = async () => {
    const response = await getApi(`api/payments/list/?createdBy=${hotelId}`);
    if (response && response.status === 200) {
      const payments = response?.data?.paymentsData || [];
      setSuccessfulPayments(payments);
      // Sincronizar el paywall: si no queda nada vigente (ej. el trial venció),
      // marcamos expired en localStorage para que el layout bloquee el resto del panel.
      try {
        const hotelDataStr = localStorage.getItem('hotelData');
        if (hotelDataStr) {
          const hotelData = JSON.parse(hotelDataStr);
          if (hotelData.role !== 'admin') {
            const vigente = payments.some((p) => p.status === 'active' || p.status === 'expiresSoon');
            hotelData.subscriptionStatus = vigente ? 'active' : 'expired';
            localStorage.setItem('hotelData', JSON.stringify(hotelData));
          }
        }
      } catch (e) {
        /* noop */
      }
    }
  };

  // Activar el período de prueba gratis de un plan (sin pagar).
  const handleStartTrial = async (plan) => {
    try {
      const response = await postApi(`api/payments/startTrial`, {
        subscription_id: plan._id,
        createdBy: hotelId
      });
      if (response && response.status === 201) {
        toast.success(response?.data?.message || 'Período de prueba activado');
        try {
          const hotelDataStr = localStorage.getItem('hotelData');
          if (hotelDataStr) {
            const hotelData = JSON.parse(hotelDataStr);
            hotelData.subscriptionStatus = 'active';
            localStorage.setItem('hotelData', JSON.stringify(hotelData));
          }
        } catch (e) {
          /* noop */
        }
        await getAllPaymentsDetails();
      } else {
        toast.error(response?.data?.error || response?.response?.data?.error || 'No se pudo activar la prueba gratis');
      }
    } catch (error) {
      toast.error(error?.response?.data?.error || 'No se pudo activar la prueba gratis');
    }
  };

  // El modal de pago (`PaymentModel`) se encarga ahora directamente de
  // crear el pago en el backend (Stripe Checkout o flujo manual con
  // comprobante). Aquí sólo refrescamos la lista de pagos del hotel
  // cuando el modal nos avisa que terminó.
  const addPayments = async (values) => {
    try {
      // El trial NO bloquea la compra: pagar durante la prueba es justamente el flujo esperado.
      const isAlreadyPurchased = successfulPayments.some(
        (payment) => payment.paymentMethod !== 'trial' && (payment.status === 'active' || payment.status === 'expiresSoon')
      );
      if (isAlreadyPurchased && !values?._alreadyPersisted) {
        toast.error('No puedes comprar otro plan, ya tienes uno activo');
        return;
      }
      // Solo refrescamos — la persistencia ya la hizo el modal.
      await getAllPaymentsDetails();
    } catch (error) {
      console.log(error);
    }
  };

  const handleBuyNowClick = async (data) => {
    setPurchaseData(data);
    handleOpenPayment();
  };

  useEffect(() => {
    getAllSubscriptionData();
    getAllPaymentsDetails();
    // eslint-disable-next-line
  }, []);

  // Plan pagado y vigente sobre un paquete concreto (excluye el trial)
  const isPaymentSuccessful = (itemId) => {
    return successfulPayments.some(
      (payment) =>
        payment.paymentMethod !== 'trial' &&
        payment.subscription_id === itemId &&
        (payment.status === 'active' || payment.status === 'expiresSoon')
    );
  };

  // Estado del período de prueba
  const trialUsed = successfulPayments.some((payment) => payment.paymentMethod === 'trial');
  const activeTrial = successfulPayments.find(
    (payment) => payment.paymentMethod === 'trial' && (payment.status === 'active' || payment.status === 'expiresSoon')
  );
  const trialDaysLeft = activeTrial?.endDate ? Math.max(0, Math.ceil((new Date(activeTrial.endDate) - new Date()) / 86400000)) : 0;
  const hasAnyVigente = successfulPayments.some((payment) => payment.status === 'active' || payment.status === 'expiresSoon');
  const trialPlan = activeTrial ? subscriptionData.find((plan) => plan._id === activeTrial.subscription_id) : null;

  // Función para obtener el icono según el nombre del plan
  const getPlanIcon = (title) => {
    const titleLower = title?.toLowerCase() || '';
    if (titleLower.includes('basic') || titleLower.includes('básico')) {
      return <BusinessIcon sx={{ fontSize: 40 }} />;
    } else if (titleLower.includes('pro') || titleLower.includes('premium')) {
      return <StarIcon sx={{ fontSize: 40 }} />;
    } else if (titleLower.includes('enterprise') || titleLower.includes('empresarial')) {
      return <DiamondIcon sx={{ fontSize: 40 }} />;
    }
    return <SubscriptionIcon sx={{ fontSize: 40 }} />;
  };

  // Función para obtener el color del plan
  const getPlanColor = (title) => {
    const titleLower = title?.toLowerCase() || '';
    if (titleLower.includes('basic') || titleLower.includes('básico')) {
      return theme.palette.info.main;
    } else if (titleLower.includes('pro') || titleLower.includes('premium')) {
      return theme.palette.warning.main;
    } else if (titleLower.includes('enterprise') || titleLower.includes('empresarial')) {
      return theme.palette.error.main;
    }
    return theme.palette.primary.main;
  };

  // Función para determinar si es el plan recomendado
  const isRecommended = (title) => {
    const titleLower = title?.toLowerCase() || '';
    return titleLower.includes('pro') || titleLower.includes('premium');
  };

  // Calcular estadísticas
  const totalPlans = subscriptionData?.length || 0;
  // Banner verde solo para planes pagados; el trial tiene su propio banner.
  const activePlan = successfulPayments?.find(
    (payment) => payment.paymentMethod !== 'trial' && (payment.status === 'active' || payment.status === 'expiresSoon')
  );

  return (
    <>
      <PaymentModel
        openPayment={openPayment}
        handleClosePayment={handleClosePayment}
        purchaseData={purchaseData}
        addPayments={addPayments}
      />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 3
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Planes de Suscripción
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Elige el plan perfecto para tu hotel y desbloquea todas las funcionalidades
              </Typography>
            </Box>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <SubscriptionIcon sx={{ fontSize: 60, opacity: 0.3 }} />
            </Box>
          </Stack>
        </Paper>

        {/* Trial Banner */}
        {activeTrial && (
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.12)} 0%, ${alpha(
                theme.palette.warning.main,
                0.05
              )} 100%)`,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`
            }}
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.warning.main, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <ScheduleIcon sx={{ color: theme.palette.warning.main, fontSize: 24 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight={600} sx={{ color: theme.palette.warning.dark }}>
                  Período de prueba: {activeTrial.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {trialDaysLeft > 0
                    ? `Te ${
                        trialDaysLeft === 1 ? 'queda 1 día' : `quedan ${trialDaysLeft} días`
                      } gratis. Paga antes de que venza para no perder el acceso.`
                    : 'Tu período de prueba venció. Paga ahora para continuar usando el sistema.'}
                </Typography>
              </Box>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Chip
                  label={trialDaysLeft > 0 ? `${trialDaysLeft} días restantes` : 'Vencido'}
                  color="warning"
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
                {trialPlan && (
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => handleBuyNowClick(trialPlan)}
                    sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 2 }}
                  >
                    Pagar ahora
                  </Button>
                )}
              </Stack>
            </Stack>
          </Card>
        )}

        {/* Status Section */}
        {activePlan && (
          <Card
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(
                theme.palette.success.main,
                0.05
              )} 100%)`,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  backgroundColor: alpha(theme.palette.success.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <VerifiedIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  Plan Activo: {activePlan.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Tu suscripción está activa y funcionando correctamente
                </Typography>
              </Box>
              <Chip
                label={activePlan.status === 'active' ? 'Activo' : 'Por Vencer'}
                color="success"
                variant="filled"
                sx={{ fontWeight: 600 }}
              />
            </Stack>
          </Card>
        )}

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[6]
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <BusinessIcon sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="primary">
                    {totalPlans}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Planes Disponibles
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[6]
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <SecurityIcon sx={{ color: theme.palette.success.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="success.main">
                    {activePlan ? 'Sí' : 'No'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Plan Activo
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[6]
                }
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    backgroundColor: alpha(theme.palette.info.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <TrendingUpIcon sx={{ color: theme.palette.info.main, fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={600} color="info.main">
                    SegoCloud
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Proveedor PMS
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Plans Grid */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            Selecciona tu Plan Ideal
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            Todos los planes incluyen soporte técnico 24/7 y actualizaciones automáticas
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {subscriptionData?.map((item) => {
            const planColor = getPlanColor(item.title);
            const isPurchased = isPaymentSuccessful(item._id);
            const recommended = isRecommended(item.title);
            const isTrialingThisPlan = activeTrial && activeTrial.subscription_id === item._id;
            const canStartTrial = Number(item.freeDays) > 0 && !trialUsed && !hasAnyVigente;

            return (
              <Grid item lg={4} md={6} xs={12} key={item._id}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    position: 'relative',
                    overflow: 'visible',
                    boxShadow: recommended ? theme.shadows[12] : theme.shadows[4],
                    border: recommended ? `2px solid ${planColor}` : `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    transition: 'all 0.3s ease',
                    transform: recommended ? 'scale(1.02)' : 'scale(1)',
                    '&:hover': {
                      transform: recommended ? 'scale(1.04)' : 'scale(1.02)',
                      boxShadow: theme.shadows[16]
                    }
                  }}
                >
                  {/* Recommended Badge */}
                  {recommended && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1
                      }}
                    >
                      <Chip
                        label="Más Popular"
                        color="warning"
                        size="small"
                        icon={<StarIcon sx={{ fontSize: 16 }} />}
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          boxShadow: theme.shadows[4]
                        }}
                      />
                    </Box>
                  )}

                  {/* Plan Header */}
                  <Box
                    sx={{
                      background: `linear-gradient(135deg, ${alpha(planColor, 0.1)} 0%, ${alpha(planColor, 0.05)} 100%)`,
                      p: 3,
                      textAlign: 'center',
                      borderBottom: `1px solid ${alpha(planColor, 0.1)}`
                    }}
                  >
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: alpha(planColor, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 2,
                        border: `3px solid ${alpha(planColor, 0.2)}`
                      }}
                    >
                      <Box sx={{ color: planColor }}>{getPlanIcon(item.title)}</Box>
                    </Box>

                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: planColor }}>
                      {item.title}
                    </Typography>
                  </Box>

                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    {/* Pricing */}
                    <Box sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.5}>
                        <Typography variant="h3" fontWeight={700} color="text.primary">
                          ${item.amount}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          USD
                        </Typography>
                      </Stack>

                      <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} sx={{ mt: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                        <Typography variant="body2" color="text.secondary">
                          {item.days} días de duración
                        </Typography>
                      </Stack>

                      {Number(item.freeDays) > 0 && (
                        <Chip
                          size="small"
                          icon={<CheckIcon sx={{ fontSize: 16 }} />}
                          label={`${item.freeDays} días gratis de prueba`}
                          sx={{
                            mt: 1,
                            fontWeight: 700,
                            backgroundColor: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main
                          }}
                        />
                      )}
                    </Box>

                    <Divider sx={{ mb: 3 }} />

                    {/* Description */}
                    <Box
                      sx={{
                        maxHeight: 120,
                        overflowY: 'auto',
                        mb: 3,
                        '&::-webkit-scrollbar': {
                          width: 4
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: alpha(theme.palette.primary.main, 0.2),
                          borderRadius: 2
                        }
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        {item.description}
                      </Typography>
                    </Box>

                    {/* Features (you can add specific features here) */}
                    <Stack spacing={1} sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                        <Typography variant="body2">Soporte 24/7</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                        <Typography variant="body2">Actualizaciones automáticas</Typography>
                      </Stack>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CheckIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                        <Typography variant="body2">Backup en la nube</Typography>
                      </Stack>
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Stack spacing={1.5} sx={{ width: '100%' }}>
                      {canStartTrial && (
                        <Button
                          fullWidth
                          size="large"
                          variant="contained"
                          color="success"
                          onClick={() => handleStartTrial(item)}
                          startIcon={<StarIcon />}
                          sx={{
                            py: 1.5,
                            borderRadius: 3,
                            fontWeight: 600,
                            fontSize: '1rem',
                            textTransform: 'none'
                          }}
                        >
                          Probar {item.freeDays} días gratis
                        </Button>
                      )}
                      <Button
                        fullWidth
                        size="large"
                        variant={isPurchased || canStartTrial ? 'outlined' : 'contained'}
                        color={isPurchased ? 'success' : 'primary'}
                        onClick={() => handleBuyNowClick(item)}
                        disabled={isPurchased}
                        startIcon={isPurchased ? <VerifiedIcon /> : <ShoppingCartIcon />}
                        sx={{
                          py: 1.5,
                          borderRadius: 3,
                          fontWeight: 600,
                          fontSize: '1rem',
                          textTransform: 'none',
                          boxShadow: isPurchased || canStartTrial ? 'none' : theme.shadows[3],
                          backgroundColor: isPurchased || canStartTrial ? 'transparent' : planColor,
                          borderColor: canStartTrial ? planColor : undefined,
                          color: canStartTrial ? planColor : undefined,
                          '&:hover': {
                            backgroundColor: isPurchased || canStartTrial ? 'transparent' : alpha(planColor, 0.8),
                            boxShadow: isPurchased || canStartTrial ? 'none' : theme.shadows[6]
                          }
                        }}
                      >
                        {isPurchased ? 'Plan Adquirido' : isTrialingThisPlan ? 'Pagar ahora' : 'Adquirir Plan'}
                      </Button>
                    </Stack>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </>
  );
};

export default HotelSubscriptionsDetails;
